import Vue from 'vue'
import * as types from './mutation-types'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { ActionTree } from 'vuex'
import RootState from '@vue-storefront/core/types/RootState'
import OrderState from '../types/OrderState'
import { Order } from '../types/Order'
import { isOnline } from '@vue-storefront/core/lib/search'
import i18n from '@vue-storefront/i18n'
import { TaskQueue } from '@vue-storefront/core/lib/sync'
import { sha3_224 } from 'js-sha3'
import { Logger } from '@vue-storefront/core/lib/logger'
import config from 'config'
import getApiEndpointUrl from '@vue-storefront/core/helpers/getApiEndpointUrl'
import rootStore from '@vue-storefront/core/store'
import { emitOrderAfterPlaced } from '../helpers/orderProgress'

const actions: ActionTree<OrderState, RootState> = {
  /**
   * Place order - send it to service worker queue
   * @param {Object} commit method
   * @param {Order} order order data to be send
   */
  async placeOrder ({ commit, getters, dispatch }, order: Order) {
    // Check if order is already processed/processing
    const currentOrderHash = sha3_224(JSON.stringify(order))
    const isAlreadyProcessed = getters.getSessionOrderHashes.includes(currentOrderHash)
    if (isAlreadyProcessed) return
    commit(types.ORDER_ADD_SESSION_STAMPS, order)
    commit(types.ORDER_ADD_SESSION_ORDER_HASH, currentOrderHash)

    const storeView = currentStoreView()
    if (storeView.storeCode) {
      order.store_code = storeView.storeCode
    }

    Vue.prototype.$bus.$emit('order-before-placed', { order: order })
    if (!config.orders.directBackendSync || !isOnline()) {
      commit(types.ORDER_PLACE_ORDER, order)
      emitOrderAfterPlaced(Vue.prototype.$bus, { order: order })
      return {
        resultCode: 200
      }
    } else {
      Vue.prototype.$bus.$emit('notification-progress-start', i18n.t('Processing order...'))
      const endpoint = getApiEndpointUrl(config.orders, 'endpoint')
      // K2-3074: ms-api takes the order's customer from the token (none for guests) and places a customer's cart only with it.
      // TaskQueue sends the token as a Bearer header (K2-3138).
      return await TaskQueue.execute({ url: endpoint, // sync the order
        payload: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          mode: 'cors',
          body: JSON.stringify(order)
        }
      })
        .then(result => {
          // TaskQueue answers {code} when the token refresh has failed for good
          const resultCode = result.resultCode !== undefined ? result.resultCode : result.code
          if (resultCode === 200) {
            order.transmited = true
            commit(types.ORDER_PLACE_ORDER, order) // archive this order but not trasmit it second time
            commit(types.ORDER_LAST_ORDER_WITH_CONFIRMATION, { order: order, confirmation: result.result })
            // K2-3073: the loader stays on for the payment; the listener stops it.
            emitOrderAfterPlaced(Vue.prototype.$bus, { order: order, confirmation: result.result })

            return result
          } else if (resultCode === 400 || resultCode === 422) {
            commit(types.ORDER_REMOVE_SESSION_ORDER_HASH, currentOrderHash)

            let contactEmail = 'info@korzyna.com'
            try {
              let location = rootStore.getters['locations/getActiveLocation']
              if (location) {
                contactEmail = location.office_email
              }
            } catch (e) {
              Logger.error('Error getting location', e)()
            }

            Logger.error('Internal validation error; Order entity is not compliant with the schema: ' + JSON.stringify(result.result), 'order')()
            dispatch('notification/spawnNotification', {
              type: 'error',
              message: i18n.t('Please check if all required fields are filled in. Please contact us on {email}', { email: contactEmail }),
              action1: { label: i18n.t('OK') }
            }, {root: true})

            order.transmited = true // we don't want to enqueue it
            commit(types.ORDER_PLACE_ORDER, order) // archive this order but not trasmit it second time
            Vue.prototype.$bus.$emit('notification-progress-stop')

            return result
          } else if (resultCode === 401 || resultCode === 403) {
            // K2-3074: another customer's cart (403), or a token that couldn't be
            // refreshed (401) - a retry can't succeed, so don't queue it.
            commit(types.ORDER_REMOVE_SESSION_ORDER_HASH, currentOrderHash)
            dispatch('notification/spawnNotification', {
              type: 'error',
              message: i18n.t(resultCode === 403 ? 'The cart belongs to another customer' : 'Your session has expired. Sign in and place the order again.'),
              action1: { label: i18n.t('OK') }
            }, {root: true})

            order.transmited = true // we don't want to enqueue it
            commit(types.ORDER_PLACE_ORDER, order)
            Vue.prototype.$bus.$emit('notification-progress-stop')

            return { ...result, resultCode } // checkout clears the cart when resultCode is missing
          }

          throw new Error('Unhandled place order request error')
        })
        .catch(e => {
          commit(types.ORDER_REMOVE_SESSION_ORDER_HASH, currentOrderHash)

          dispatch('notification/spawnNotification', {
            type: 'error',
            message: i18n.t('The order can not be transferred because of server error. Order has been queued'),
            action1: { label: i18n.t('OK') }
          }, {root: true})

          order.transmited = false // enqueue order
          commit(types.ORDER_PLACE_ORDER, order) // archive this order and trasmit it next time the QUEUE is published

          Vue.prototype.$bus.$emit('notification-progress-stop')

          throw e
        })
    }
  }
}

export default actions
